import { useLazyQuery } from "@apollo/client"
import { CountryIsoCode } from "@oysterhr/schema"
import { gql } from "../gql"

const OYSTER_PAY_BAND_AND_MARKET_DATA_SALARY_BENCHMARKS_QUERY = gql(`
  query OysterPayBandsAndMarketDataSalaryBenchmarksQuery(
    $roleTypeId: ID!
    $countryCode: CountryIsoCode!
    $seniorityLevelId: ID!
    $currencyCode: String
    $budgetAmount: Float
    # I need to repeat countryCode here because the input needs an array
    $countryCodes: [CountryIsoCode!]!
    $includeMarketData: Boolean!
  ) {
    compensationInsights {
      salaryBenchmarksForAllLevels(
        providerIds: [OYSTER_POLICY]
        roleTypeId: $roleTypeId
        countryCode: $countryCode
        compensationInsightsSeniorityLevelId: $seniorityLevelId
        currencyCode: $currencyCode
        budgetAmount: $budgetAmount
      ) {
        low {
          ...MoneyDetails
        }
        mid {
          ...MoneyDetails
        }
        high {
          ...MoneyDetails
        }
        trustLevel
        providerId
        level
      }

      oysterBandIndexes(countryCodes: $countryCodes) {
        countryCode
        obi
      }
      
      salaryBenchmarksForAdjacentLevels(
        providerIds: [FIGURES_API]
        roleTypeId: $roleTypeId
        countryCode: $countryCode
        compensationInsightsSeniorityLevelId: $seniorityLevelId
        currencyCode: $currencyCode
      ) @include(if: $includeMarketData) {
        trustLevel
        providerId
        level
        percentiles {
          p10 {
            ...MoneyDetails
          }
          p25 {
            ...MoneyDetails
          }
          p50 {
            ...MoneyDetails
          }
          p75 {
            ...MoneyDetails
          }
          p90 {
            ...MoneyDetails
          }
        }
      }
    }
  }
  
  fragment MoneyDetails on Money {
  currencyCode
  formatted
  subunit
  subunitToUnit
}
`)

const useOysterPayBandsAndMarketDataSalaryBenchmarksQuery = () => {
  const [lazyQuery, { data, ...rest }] = useLazyQuery(
    OYSTER_PAY_BAND_AND_MARKET_DATA_SALARY_BENCHMARKS_QUERY,
  )

  const getBenchmark = (args: {
    budgetAmount?: number
    countryCode: CountryIsoCode
    currencyCode?: string
    roleTypeId: string
    seniorityLevelId: string
    includeMarketData: boolean
  }) => {
    return lazyQuery({
      variables: {
        roleTypeId: args.roleTypeId,
        countryCode: args.countryCode,
        countryCodes: [args.countryCode],
        currencyCode: args?.currencyCode,
        budgetAmount: args?.budgetAmount,
        seniorityLevelId: args?.seniorityLevelId,
        includeMarketData: args?.includeMarketData,
      },
    })
  }

  return [
    getBenchmark,
    {
      oysterPayBandBenchmarks: data?.compensationInsights?.salaryBenchmarksForAllLevels ?? [],
      marketDataBenchmarks: data?.compensationInsights?.salaryBenchmarksForAdjacentLevels ?? [],
      oysterBandIndex: data?.compensationInsights?.oysterBandIndexes?.[0],
      ...rest,
    },
  ] as const
}

export { useOysterPayBandsAndMarketDataSalaryBenchmarksQuery }
